import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';

const WAKE_MESSAGES = {
  capybara: {
    headline: 'Woow, du gjorde det! 🎉',
    subline: 'Jag kliver upp ur badet och firar med dig ♨️',
    emoji: '♨️',
    particles: ['🎉', '💧', '✨', '🌸'],
  },
  corgi: {
    headline: 'Gäsp… du FIXADE det! 🎉',
    subline: 'Corgus vaknar och viftar på svansen 🐾',
    emoji: '🐾',
    particles: ['🎉', '⭐', '💖', '🦴'],
  },
  red_panda: {
    headline: 'Färdig-stickat! 🎉',
    subline: 'Jag lägger ifrån mig stickorna — vi klarade det! 🧶',
    emoji: '🧶',
    particles: ['🎉', '🧶', '✨', '🍵'],
  },
  frog: {
    headline: 'Meditationen är klar ✨',
    subline: 'Vi andas ut tillsammans — bra jobbat! 🧘',
    emoji: '💮',
    particles: ['🎉', '💮', '🪷', '✨'],
  },
};

const DEFAULT = WAKE_MESSAGES.corgi;

export default function FocusWakeUp({ pet, pointsEarned, leveledUp, onClose }) {
  const [closing, setClosing] = useState(false);
  const config = (pet && WAKE_MESSAGES[pet.pet_type]) || DEFAULT;

  useEffect(() => {
    const t = setTimeout(() => handleClose(), 6000);
    return () => clearTimeout(t);
     
  }, []);

  const handleClose = () => {
    setClosing(true);
    setTimeout(onClose, 300);
  };

  // 18 floating celebration particles with random positions
  const particles = Array.from({ length: 18 }, (_, i) => ({
    emoji: config.particles[i % config.particles.length],
    left: Math.random() * 90 + 5,
    delay: Math.random() * 2,
    duration: 2.5 + Math.random() * 1.5,
    size: 18 + Math.random() * 22,
  }));

  return (
    <div
      className={`fixed inset-0 z-[100] flex items-center justify-center transition-opacity duration-300 ${closing ? 'opacity-0' : 'opacity-100'}`}
      data-testid="focus-wake-up"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Particles */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {particles.map((p, i) => (
          <span
            key={i}
            className="absolute animate-confetti-fall"
            style={{
              left: `${p.left}%`,
              top: '-10%',
              fontSize: `${p.size}px`,
              animationDelay: `${p.delay}s`,
              animationDuration: `${p.duration}s`,
            }}
          >
            {p.emoji}
          </span>
        ))}
      </div>

      {/* Dialog */}
      <div
        className="relative neo-card p-8 bg-[#FFFAF0] max-w-md w-[90%] text-center animate-bounce-in"
        data-testid="focus-wake-up-card"
      >
        <button
          onClick={handleClose}
          className="absolute top-3 right-3 neo-btn p-1 bg-white"
          data-testid="wake-up-close"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Pet avatar with wake-up animation */}
        {pet && (
          <div className="relative inline-block mb-4">
            <div className="w-32 h-32 rounded-full border-[4px] border-black overflow-hidden shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] animate-wake-up mx-auto">
              <img src={pet.image} alt={pet.name} className="w-full h-full object-cover" />
            </div>
            <span className="absolute -top-2 -right-2 text-3xl animate-bounce-in" style={{ animationDelay: '0.4s' }}>
              {config.emoji}
            </span>
          </div>
        )}

        <h2 className="text-2xl font-bold font-['Fredoka'] mb-1">{config.headline}</h2>
        <p className="text-sm opacity-80 mb-4">{config.subline}</p>

        {/* Points earned */}
        {pointsEarned > 0 && (
          <div className="rounded-xl border-2 border-black p-3 bg-[#FDF0D5] mb-3">
            <p className="text-xs font-bold uppercase tracking-wider opacity-60">Du tjänade</p>
            <p className="text-3xl font-bold font-['Fredoka'] text-[#F0A020]">+{pointsEarned} p</p>
          </div>
        )}
        {leveledUp && (
          <div className="rounded-xl border-2 border-black p-3 bg-[#B9FBC0] mb-3 animate-pulse-soft">
            <p className="font-bold">⭐ {pet?.name || 'Ditt husdjur'} gick upp en nivå!</p>
          </div>
        )}

        <button
          onClick={handleClose}
          className="neo-btn px-6 py-3 bg-[#A2D2FF] font-bold mt-2"
          data-testid="wake-up-continue"
        >
          Dags för paus ☕
        </button>
      </div>
    </div>
  );
}
